import type { StyleCatalogResponse, StyleOption } from '../../../shared/api/contracts/styleTransferSchemas';

interface StyleCacheEntry {
  cacheKey: string;
  pages: StyleCatalogResponse[];
  storedAt: string;
  expiresAt: string;
  storageBytes: number;
}

const DB_NAME = 'photo-studio-styles-cache';
const STORE_NAME = 'styles';
const DB_VERSION = 2;

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const MAX_CACHE_ENTRIES = 500;
const MAX_STORAGE_BYTES = 500 * 1024 * 1024; // 500 MB

const supportsIndexedDB = typeof indexedDB !== 'undefined';

let dbPromise: Promise<IDBDatabase> | null = null;
const memoryStore = new Map<string, StyleCacheEntry>();

const textEncoder = typeof TextEncoder !== 'undefined' ? new TextEncoder() : null;

function estimateEntryBytes(pages: StyleCatalogResponse[]): number {
  try {
    const serialized = JSON.stringify(pages);
    if (textEncoder) {
      return textEncoder.encode(serialized).length;
    }
    return serialized.length;
  } catch {
    return 0;
  }
}

function openDatabase(): Promise<IDBDatabase> {
  if (!supportsIndexedDB) {
    return Promise.reject(new Error('IndexedDB is not available in this environment.'));
  }
  if (dbPromise) {
    return dbPromise;
  }

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME, { keyPath: 'cacheKey' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Failed to open styles cache'));
  });

  return dbPromise;
}

function now(): number {
  return Date.now();
}

function buildEntry(cacheKey: string, pages: StyleCatalogResponse[]): StyleCacheEntry {
  const storedAt = new Date().toISOString();
  const expiresAt = new Date(Date.now() + CACHE_TTL_MS).toISOString();
  return {
    cacheKey,
    pages,
    storedAt,
    expiresAt,
    storageBytes: estimateEntryBytes(pages)
  };
}

function sortPages(pages: StyleCatalogResponse[]): StyleCatalogResponse[] {
  return [...pages].sort((a, b) => a.pageInfo.page - b.pageInfo.page);
}

function mergePages(
  existing: StyleCatalogResponse[],
  next: StyleCatalogResponse[]
): StyleCatalogResponse[] {
  if (!existing.length) {
    return sortPages(next);
  }
  const pageMap = new Map<number, StyleCatalogResponse>();
  existing.forEach((page) => {
    pageMap.set(page.pageInfo.page, page);
  });
  next.forEach((page) => {
    pageMap.set(page.pageInfo.page, page);
  });
  return sortPages(Array.from(pageMap.values()));
}

export interface CachedStylesResult {
  pages: StyleCatalogResponse[];
  expiresAt: string;
  storedAt: string;
}

type LegacyStyleCacheEntry = {
  cacheKey: string;
  styles: StyleOption[];
  storedAt: string;
  expiresAt: string;
  storageBytes: number;
};

function normalizeEntry(entry: StyleCacheEntry | LegacyStyleCacheEntry | null): StyleCacheEntry | null {
  if (!entry) {
    return null;
  }
  if (Array.isArray((entry as StyleCacheEntry).pages)) {
    return entry as StyleCacheEntry;
  }
  const legacy = entry as LegacyStyleCacheEntry;
  if (!Array.isArray(legacy.styles)) {
    return null;
  }
  const fallbackPage: StyleCatalogResponse = {
    items: legacy.styles,
    pageInfo: {
      page: 0,
      size: legacy.styles.length || 1,
      totalElements: legacy.styles.length,
      totalPages: 1,
      hasNext: false,
      hasPrevious: false
    }
  };
  return {
    cacheKey: legacy.cacheKey,
    pages: [fallbackPage],
    storedAt: legacy.storedAt,
    expiresAt: legacy.expiresAt,
    storageBytes: legacy.storageBytes
  };
}

async function readEntry(cacheKey: string): Promise<StyleCacheEntry | null> {
  if (!supportsIndexedDB) {
    return normalizeEntry(memoryStore.get(cacheKey) ?? null);
  }

  const db = await openDatabase();
  const raw = await new Promise<StyleCacheEntry | LegacyStyleCacheEntry | null>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(cacheKey);
    request.onsuccess = () => resolve((request.result as StyleCacheEntry | LegacyStyleCacheEntry | undefined) ?? null);
    request.onerror = () => reject(request.error ?? new Error('Failed to read styles cache entry'));
  });
  return normalizeEntry(raw);
}

async function writeEntry(entry: StyleCacheEntry): Promise<void> {
  if (!supportsIndexedDB) {
    memoryStore.set(entry.cacheKey, entry);
    trimMemoryStore();
    return;
  }

  const db = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.put(entry);
    request.onsuccess = () => resolve();
    request.onerror = () =>
      reject(request.error ?? transaction.error ?? new Error('Failed to write styles cache'));
  });

  await enforceLimits();
}

export async function getCachedStyles(cacheKey: string): Promise<CachedStylesResult | null> {
  const entry = await readEntry(cacheKey);
  if (!entry) {
    return null;
  }
  return {
    pages: entry.pages,
    expiresAt: entry.expiresAt,
    storedAt: entry.storedAt
  };
}

export async function getCachedStylesPage(
  cacheKey: string,
  pageNumber: number
): Promise<StyleCatalogResponse | null> {
  const entry = await readEntry(cacheKey);
  if (!entry) {
    return null;
  }
  return entry.pages.find((page) => page.pageInfo.page === pageNumber) ?? null;
}

export async function saveStylesCachePages(
  cacheKey: string,
  pages: StyleCatalogResponse[]
): Promise<void> {
  if (!pages.length) {
    return;
  }
  const existing = await readEntry(cacheKey);
  const merged = mergePages(existing?.pages ?? [], pages);
  const entry = buildEntry(cacheKey, merged);
  await writeEntry(entry);
}

function trimMemoryStore() {
  const entries = Array.from(memoryStore.values()).sort((a, b) =>
    a.storedAt < b.storedAt ? 1 : -1
  );
  let totalBytes = entries.reduce((sum, item) => sum + item.storageBytes, 0);
  for (let i = MAX_CACHE_ENTRIES; i < entries.length; i += 1) {
    memoryStore.delete(entries[i].cacheKey);
  }
  if (totalBytes <= MAX_STORAGE_BYTES) {
    return;
  }
  for (const entry of entries) {
    if (totalBytes <= MAX_STORAGE_BYTES) {
      break;
    }
    memoryStore.delete(entry.cacheKey);
    totalBytes -= entry.storageBytes;
  }
}

async function enforceLimits(): Promise<void> {
  if (!supportsIndexedDB) {
    return;
  }

  const db = await openDatabase();
  const entries = await new Promise<StyleCacheEntry[]>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();
    request.onsuccess = () => resolve((request.result ?? []) as StyleCacheEntry[]);
    request.onerror = () => reject(request.error ?? new Error('Failed to list styles cache entries'));
  });

  const sorted = entries.sort((a, b) => (a.storedAt < b.storedAt ? 1 : -1));
  let totalBytes = sorted.reduce((sum, item) => sum + item.storageBytes, 0);
  const toDelete: string[] = [];

  if (sorted.length > MAX_CACHE_ENTRIES) {
    const overflow = sorted.slice(MAX_CACHE_ENTRIES);
    overflow.forEach((item) => {
      toDelete.push(item.cacheKey);
      totalBytes -= item.storageBytes;
    });
  }

  if (totalBytes > MAX_STORAGE_BYTES) {
    for (let i = sorted.length - 1; i >= 0 && totalBytes > MAX_STORAGE_BYTES; i -= 1) {
      const item = sorted[i];
      if (toDelete.includes(item.cacheKey)) {
        continue;
      }
      toDelete.push(item.cacheKey);
      totalBytes -= item.storageBytes;
    }
  }

  if (!toDelete.length) {
    return;
  }

  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    toDelete.forEach((key) => store.delete(key));
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error('Failed to trim styles cache'));
  });
}

export async function pruneExpiredStylesCache(): Promise<void> {
  const cutoff = now();
  if (!supportsIndexedDB) {
    for (const [key, entry] of memoryStore.entries()) {
      if (new Date(entry.expiresAt).getTime() <= cutoff) {
        memoryStore.delete(key);
      }
    }
    return;
  }

  const db = await openDatabase();
  const entries = await new Promise<StyleCacheEntry[]>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();
    request.onsuccess = () => resolve((request.result ?? []) as StyleCacheEntry[]);
    request.onerror = () => reject(request.error ?? new Error('Failed to scan styles cache'));
  });

  const toDelete = entries
    .filter((entry) => new Date(entry.expiresAt).getTime() <= cutoff)
    .map((entry) => entry.cacheKey);

  if (!toDelete.length) {
    return;
  }

  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    toDelete.forEach((key) => store.delete(key));
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error('Failed to prune styles cache'));
  });
}

export function isCacheEntryExpired(entry: CachedStylesResult | null): boolean {
  if (!entry) {
    return true;
  }
  return new Date(entry.expiresAt).getTime() <= now();
}

export function buildStylesCacheKey(params: {
  tokenType?: 'device' | 'user' | null;
  locale?: string;
  pageSize?: number;
}): string {
  const tokenType = params.tokenType ?? 'anonymous';
  const locale = params.locale ?? 'default';
  const pageSize = Number.isFinite(params.pageSize) ? `::size=${params.pageSize}` : '';
  return `styles::${tokenType}::${locale}${pageSize}`;
}
